import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { agent } from "../../../utils/agent";
import * as API from "../../../utils/apiPath";

/* ------------------------------------------------------------------
   THUNKS : API CALLS USED IN Home Page Component
-------------------------------------------------------------------*/

// 1️⃣ Home page data
export const loadHomePageData = createAsyncThunk(
  "home/loadHomePageData",
  async (id, { rejectWithValue }) => {
    try {
      const res = await agent.get(API.CMS_PAGE + id);
      return res.data?.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);

// 2️⃣ Leaders
export const loadLeaderData = createAsyncThunk(
  "home/loadLeaderData",
  async (limit, { rejectWithValue }) => {
    try {
      const res = await agent.get(`${API.LEADER_DATA}?limit=${limit}`);
      return res.data?.data || [];
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);

// 3️⃣ Testimonials
export const loadTestimonialsData = createAsyncThunk(
  "home/loadTestimonialsData",
  async (limit, { rejectWithValue }) => {
    try {
      const res = await agent.get(`${API.TESTIMONIALS_DATA}?limit=${limit}`);
      return res.data?.data || [];
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);

// 4️⃣ Communities
export const loadCommunityData = createAsyncThunk(
  "home/loadCommunityData",
  async (limit, { rejectWithValue }) => {
    try {
      const res = await agent.get(`${API.COMMUNITY_DATA}?limit=${limit}`);
      return res.data?.data || [];
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);

// 5️⃣ Brand logos
export const getBrandData = createAsyncThunk(
  "home/getBrandData",
  async (_, { rejectWithValue }) => {
    try {
      const res = await agent.get(API.BRAND);
      return res.data?.data || [];
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);

// 6️⃣ Email Verification
export const sendEmailForVerification = createAsyncThunk(
  "home/sendEmailForVerification",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await agent.post(API.SEND_EMAIL_FOR_VERIFICATION, payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);

/* ------------------------------------------------------------------
   SLICE
-------------------------------------------------------------------*/

const homeSlice = createSlice({
  name: "home",
  initialState: {
    setting: {},
    homePageData: {},
    leaders: [],
    testimonials: [],
    community: [],
    brand: [],
    userInfo: {},
    loading: false,
    error: null,
  },

  reducers: {
    setUserInfo(state, action) {
      state.userInfo = {
        ...state.userInfo,
        ...action.payload,
      };
    },
  },

  extraReducers: (builder) => {
    builder

      /* Home Page Data */
      .addCase(loadHomePageData.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadHomePageData.fulfilled, (state, action) => {
        state.loading = false;
        state.homePageData = action.payload;
        state.setting = action.payload?.setting || {};
      })
      .addCase(loadHomePageData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* Leaders */
      .addCase(loadLeaderData.fulfilled, (state, action) => {
        state.leaders = action.payload;
      })

      /* Testimonials */
      .addCase(loadTestimonialsData.fulfilled, (state, action) => {
        state.testimonials = action.payload;
      })

      /* Community */
      .addCase(loadCommunityData.fulfilled, (state, action) => {
        state.community = action.payload;
      })

      /* Brand */
      .addCase(getBrandData.fulfilled, (state, action) => {
        state.brand = action.payload;
      })

      /* Email Verification */
      .addCase(sendEmailForVerification.fulfilled, (state, action) => {
        state.emailResponse = action.payload;
      })
      .addCase(sendEmailForVerification.rejected, (state, action) => {
        state.emailResponse = action.payload;
      });
  },
});

export const { setUserInfo } = homeSlice.actions;
export default homeSlice.reducer;
